import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { filter, map, pairwise, startWith, withLatestFrom } from 'rxjs';

import { pickWithErrorSelector } from '../../picks/selectors';
import { ComboPreventionValidator } from '../../validation/services/validators/bets/combo-prevention.validator';
import { GroupPicksComboPreventionValidator } from '../../validation/services/validators/picks/group-picks-combo-prevention-validator';
import { QuickBetActions } from '../quick-bet-actions';
import { selectQuickBetDisplayHelper, selectQuickBetPickIds, selectQuickBetPicks } from '../quick-bet.selectors';
import { QuickBetService } from './quick-bet.service';

@Injectable()
export class QuickBetComboPreventionService {
    constructor(
        private comboPreventionValidator: ComboPreventionValidator,
        private groupPicksComboPreventionValidator: GroupPicksComboPreventionValidator,
        private quickBetService: QuickBetService,
        private store: Store,
        private destroyRef: DestroyRef,
    ) {}

    init(): void {
        this.subscribeForHideQuickBetOnNonCombinablePicks();
        this.subscribeForCloseQuickBetOnSkippingUndo();
    }

    private subscribeForHideQuickBetOnNonCombinablePicks(): void {
        this.store
            .select(selectQuickBetPickIds)
            .pipe(
                withLatestFrom(this.store.select(selectQuickBetPicks)),
                map(([, quickBetPicks]) => {
                    let hasComboErrors = false;
                    hasComboErrors =
                        hasComboErrors || !!Object.keys(this.comboPreventionValidator.getRestrictions(quickBetPicks, false, false)).length;
                    hasComboErrors = hasComboErrors || !!this.groupPicksComboPreventionValidator.validate(quickBetPicks).length;

                    return hasComboErrors;
                }),
                filter((hasComboPrevention) => hasComboPrevention),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.quickBetService.toggleQuickBetVisibility(false);
                this.store.dispatch(QuickBetActions.hideQuickBet());
                this.store.dispatch(QuickBetActions.setHiddenByError({ isHidden: true }));
            });
    }

    private subscribeForCloseQuickBetOnSkippingUndo(): void {
        this.store
            .select(pickWithErrorSelector)
            .pipe(
                withLatestFrom(this.store.select(selectQuickBetDisplayHelper)),
                filter(([, displayHelper]) => displayHelper.hiddenByError),
                map(([lastPickError]) => !lastPickError.previousHadError),
                startWith(false),
                pairwise(),
                filter(([hadUndo, hasUndo]) => hadUndo && !hasUndo),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.store.dispatch(QuickBetActions.closeQuickBet({ cleanErrors: false }));
            });
    }
}
